import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import {
  getSpeakingEvents,
  createSpeakingEvent,
  updateSpeakingEvent,
  deleteSpeakingEvent,
} from '../../lib/supabase';

interface EventFormData {
  title: string;
  event_name: string;
  location: string;
  date_start: string;
  date_end: string;
  description: string;
  event_url: string;
  is_published: boolean;
}

const SpeakingEventsManager: React.FC = () => {
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState<any>(null);
  const [formData, setFormData] = useState<EventFormData>({
    title: '',
    event_name: '',
    location: '',
    date_start: '',
    date_end: '',
    description: '',
    event_url: '',
    is_published: true,
  });

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      const data = await getSpeakingEvents(true);
      setEvents(data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (event?: any) => {
    if (event) {
      setEditingEvent(event);
      setFormData({
        title: event.title,
        event_name: event.event_name,
        location: event.location,
        date_start: event.date_start?.split('T')[0] || '',
        date_end: event.date_end?.split('T')[0] || '',
        description: event.description || '',
        event_url: event.event_url || '',
        is_published: event.is_published,
      });
    } else {
      setEditingEvent(null);
      setFormData({
        title: '',
        event_name: '',
        location: '',
        date_start: '',
        date_end: '',
        description: '',
        event_url: '',
        is_published: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingEvent(null);
    setError('');
  };

  const handleSubmit = async () => {
    try {
      if (editingEvent) {
        await updateSpeakingEvent(editingEvent.id, formData);
      } else {
        await createSpeakingEvent(formData);
      }

      await loadEvents();
      handleCloseDialog();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this event?')) {
      return;
    }

    try {
      await deleteSpeakingEvent(id);
      await loadEvents();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleInputChange = (field: keyof EventFormData, value: any) => {
    setFormData({ ...formData, [field]: value });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const isUpcoming = (dateStart: string) => {
    return new Date(dateStart) > new Date();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: 'white' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography
          variant="h2"
          sx={{
            fontFamily: '"Impact", "Arial Black", sans-serif',
            fontWeight: 900,
            fontSize: '2rem',
            textTransform: 'uppercase',
            color: '#1a1a1a',
          }}
        >
          Speaking Events
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            bgcolor: '#dc143c',
            color: 'white',
            borderRadius: 0,
            fontFamily: '"Arial Black", sans-serif',
            fontWeight: 700,
            textTransform: 'uppercase',
            '&:hover': {
              bgcolor: '#b01030',
            }
          }}
        >
          New Event
        </Button>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            borderRadius: 0,
            border: '2px solid #dc143c',
          }}
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} sx={{ borderRadius: 0, border: '2px solid #dc143c', bgcolor: 'white' }}>
        <Table sx={{ bgcolor: 'white' }}>
          <TableHead sx={{ bgcolor: '#1a1a1a' }}>
            <TableRow>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontFamily: '"Arial Black", sans-serif', textTransform: 'uppercase' }}>Title</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontFamily: '"Arial Black", sans-serif', textTransform: 'uppercase' }}>Event Name</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontFamily: '"Arial Black", sans-serif', textTransform: 'uppercase' }}>Location</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontFamily: '"Arial Black", sans-serif', textTransform: 'uppercase' }}>Date</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontFamily: '"Arial Black", sans-serif', textTransform: 'uppercase' }}>Status</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontFamily: '"Arial Black", sans-serif', textTransform: 'uppercase' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody sx={{ bgcolor: 'white' }}>
            {events.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No events yet
                </TableCell>
              </TableRow>
            ) : (
              events.map((event) => (
                <TableRow
                  key={event.id}
                  sx={{
                    bgcolor: 'white',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    }
                  }}
                >
                  <TableCell sx={{ fontFamily: '"Arial", sans-serif', fontWeight: 600 }}>{event.title}</TableCell>
                  <TableCell sx={{ fontFamily: '"Arial", sans-serif' }}>{event.event_name}</TableCell>
                  <TableCell sx={{ fontFamily: '"Arial", sans-serif' }}>{event.location}</TableCell>
                  <TableCell sx={{ fontFamily: '"Arial", sans-serif' }}>
                    {formatDate(event.date_start)}
                    {event.date_end && event.date_end !== event.date_start && (
                      <> - {formatDate(event.date_end)}</>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {event.is_published ? (
                        <Chip label="Published" size="small" color="success" />
                      ) : (
                        <Chip label="Draft" size="small" color="default" />
                      )}
                      {isUpcoming(event.date_start) ? (
                        <Chip label="Upcoming" size="small" color="primary" />
                      ) : (
                        <Chip label="Past" size="small" color="default" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(event)}
                      title="Edit"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(event.id)}
                      title="Delete"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'white',
            borderRadius: 0,
            border: '3px solid #dc143c',
          }
        }}
      >
        <DialogTitle sx={{
          bgcolor: '#1a1a1a',
          color: 'white',
          fontFamily: '"Arial Black", sans-serif',
          fontWeight: 700,
          textTransform: 'uppercase',
        }}>
          {editingEvent ? 'Edit Speaking Event' : 'New Speaking Event'}
        </DialogTitle>
        <DialogContent sx={{ bgcolor: 'white', pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Title"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              required
              fullWidth
              helperText="Your talk/presentation title"
            />
            <TextField
              label="Event Name"
              value={formData.event_name}
              onChange={(e) => handleInputChange('event_name', e.target.value)}
              required
              fullWidth
              helperText="Name of the conference/event"
            />
            <TextField
              label="Location"
              value={formData.location}
              onChange={(e) => handleInputChange('location', e.target.value)}
              required
              fullWidth
              helperText="City, Country or 'Virtual'"
            />
            <TextField
              label="Start Date"
              type="date"
              value={formData.date_start}
              onChange={(e) => handleInputChange('date_start', e.target.value)}
              required
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Date"
              type="date"
              value={formData.date_end}
              onChange={(e) => handleInputChange('date_end', e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Leave empty if single-day event"
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              multiline
              rows={3}
              fullWidth
            />
            <TextField
              label="Event URL"
              value={formData.event_url}
              onChange={(e) => handleInputChange('event_url', e.target.value)}
              fullWidth
              helperText="Link to event website"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_published}
                  onChange={(e) => handleInputChange('is_published', e.target.checked)}
                />
              }
              label="Published"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ bgcolor: 'white', p: 2 }}>
          <Button
            onClick={handleCloseDialog}
            sx={{
              color: '#dc143c',
              borderRadius: 0,
              fontFamily: '"Arial Black", sans-serif',
              fontWeight: 700,
              textTransform: 'uppercase',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{
              bgcolor: '#dc143c',
              color: 'white',
              borderRadius: 0,
              fontFamily: '"Arial Black", sans-serif',
              fontWeight: 700,
              textTransform: 'uppercase',
              '&:hover': {
                bgcolor: '#b01030',
              }
            }}
          >
            {editingEvent ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SpeakingEventsManager;